import { inject, Injector } from '@angular/core'

import { chunk, difference, differenceWith, intersectionWith } from 'lodash-es'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  skip,
  take,
  takeUntil,
} from 'rxjs'

import { Action, Resource } from '@ti/contracts'
import { ApiService } from '@ti/core/api'
import { Memoize } from '@ti/core/common/decorators'
import { AccessControl } from '@ti/core/common/services/access-control'
import { BrowserSessionStorage } from '@ti/core/common/storage'
import { completeStore } from '@ti/core/common/utils/complete-store'
import { injectDestroy$ } from '@ti/core/common/utils/inject-destroy-event'
import { toSignalState } from '@ti/core/common/utils/signal-state'

export interface CachedStoreSnapshot<K = string | number> {
  id: K
  updatedAt?: Date | string
}

// TODO: Move to ../services folder
export abstract class BaseCachedStore<T extends { id: K; updatedAt?: Date }, K = string | number> {
  protected readonly accessControl = inject(AccessControl)
  protected readonly api = inject(ApiService)
  protected readonly injector = inject(Injector)
  protected readonly sessionStorage = inject(BrowserSessionStorage)
  protected readonly destroy$ = injectDestroy$()

  protected readonly store = {
    data$: new BehaviorSubject<T[]>([]),
    isFresh$: new BehaviorSubject<boolean>(false),
    isLoading$: new BehaviorSubject<boolean>(true),
    isRefreshing$: new BehaviorSubject<boolean>(false),
  }
  protected readonly dataMap = new Map<K, T>()

  protected abstract readonly CACHE_KEY: string | (() => string)
  protected abstract readonly CHECK_RESOURCE_ACCESS?: Resource

  public constructor() {
    // Execute initializer asynchronously
    Promise.resolve().then(() => this.init().catch((error) => console.error(error)))
    this.destroy$.subscribe(() => completeStore(this.store))
  }

  @Memoize()
  public get state$() {
    return {
      items: this.store.data$.asObservable(),
      isFresh: this.store.isFresh$.asObservable(),
      isLoading: this.store.isLoading$.asObservable(),
    }
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  public getAllItems(): Promise<T[]> {
    return firstValueFrom(this.state$.items)
  }

  public async refreshAll() {
    const accessAllowed = this.CHECK_RESOURCE_ACCESS
      ? await this.accessControl.check(this.CHECK_RESOURCE_ACCESS, Action.View)
      : true
    if (!accessAllowed) {
      return
    }

    const data = this.getData().map((item) => this.dataToSnapshot(item))

    // Update data by
    const snapshot = await this.loadSnapshot()

    const dataToUpdate = intersectionWith(data, snapshot, (a, b) => a.id === b.id)
    const dataToRequest = differenceWith(snapshot, dataToUpdate, (a, b) => a.id === b.id)
    const dataToDelete = differenceWith(data, dataToUpdate, (a, b) => a.id === b.id)

    // Remove data elements from the store
    if (dataToDelete.length) {
      dataToDelete.forEach((data) => this.dataMap.delete(data.id))
      this.addData([]) // Refresh the data store
    }

    const idsToReload = dataToRequest.map((data) => data.id)
    dataToUpdate.forEach((data) => {
      const snapshotData = snapshot.find((item) => item.id === data.id)
      if (snapshotData && snapshotData.updatedAt?.valueOf() !== data.updatedAt?.valueOf()) {
        idsToReload.push(data.id)
      }
    })

    await this.refreshById(idsToReload)

    if (!this.store.isFresh$.value) {
      this.store.isFresh$.next(true)
    }
  }

  public async refreshById(id: K | K[]) {
    const accessAllowed = this.CHECK_RESOURCE_ACCESS
      ? await this.accessControl.check(this.CHECK_RESOURCE_ACCESS, Action.View)
      : true
    if (!accessAllowed) {
      return
    }

    const ids = Array.isArray(id) ? id : [id]
    if (ids.length) {
      this.store.isRefreshing$.next(true)

      try {
        // TODO: Figure out how to properly handle that problem
        // Process loaded data in batches to prevent URL length issues
        let loadedData: T[] = []
        const chunks = chunk(ids, 200)
        for (const idsChunk of chunks) {
          loadedData = loadedData.concat(await this.loadById(idsChunk))
        }

        if (loadedData.length !== ids.length) {
          const loadedIds = loadedData.map((item) => item.id)
          const idsToRemove = difference(ids, loadedIds)
          idsToRemove.forEach((id) => this.dataMap.delete(id))
        }
        this.addData(loadedData)
      } finally {
        // A failed refresh must not leave the store permanently refreshing, or every
        // ensureIsFresh() awaiter blocks forever.
        this.store.isRefreshing$.next(false)
      }
    }
  }

  public triggerRefresh(id?: K | K[]) {
    if (!id) {
      this.refreshAll().catch((e) => console.error(e))
    } else {
      this.refreshById(Array.isArray(id) ? id : [id]).catch((e) => console.error(e))
    }
  }

  public ensureIsFresh() {
    return firstValueFrom(
      combineLatest([this.store.isFresh$, this.store.isLoading$, this.store.isRefreshing$]).pipe(
        takeUntil(this.destroy$),
        filter(([isFresh, isLoading, isRefreshing]) => isFresh && !isLoading && !isRefreshing),
      ),
    )
  }

  public onceRefreshed$(delay = 0) {
    return this.state$.items.pipe(takeUntil(this.destroy$), skip(1), debounceTime(delay), take(1))
  }

  protected abstract loadAll(): Promise<T[]>

  protected abstract loadById(ids: K[]): Promise<T[]>

  protected abstract loadSnapshot(): Promise<CachedStoreSnapshot<K>[]>

  protected async init() {
    const accessAllowed = this.CHECK_RESOURCE_ACCESS
      ? await this.accessControl.check(this.CHECK_RESOURCE_ACCESS, Action.View)
      : true
    if (!accessAllowed) {
      // Settle as an empty but loaded store, otherwise ensureIsFresh() never emits and
      // every awaiting consumer hangs instead of seeing zero accessible items.
      this.store.isFresh$.next(true)
      this.store.isLoading$.next(false)
      return
    }

    const cachedItems = this.getCachedData()
    if (cachedItems && cachedItems.length) {
      // Bootstrap with cached data
      this.addData(cachedItems)

      // Refresh the data in the background
      this.refreshAll().catch((error) => {
        console.error(`Cannot refresh cached store ${this.constructor.name}`, error)
      })
    } else {
      this.addData(await this.loadAll())
      this.store.isFresh$.next(true)
    }
    this.store.isLoading$.next(false)
  }

  protected getData() {
    return Array.from(this.dataMap.values())
  }

  protected addData(data: T[]) {
    data.forEach((item) => this.dataMap.set(item.id, item))
    this.store.data$.next(this.getData())
    this.setCachedData(this.getData())
  }

  protected getCachedData(): T[] {
    const cached = this.sessionStorage.getItem(
      typeof this.CACHE_KEY === 'string' ? this.CACHE_KEY : this.CACHE_KEY(),
    )
    if (cached) {
      return JSON.parse(cached) as T[]
    }
    return []
  }

  protected setCachedData(data: T[]) {
    this.sessionStorage.setItem(
      typeof this.CACHE_KEY === 'string' ? this.CACHE_KEY : this.CACHE_KEY(),
      JSON.stringify(data),
    )
  }

  protected dataToSnapshot(data: T): CachedStoreSnapshot<K> {
    return data
  }
}
